/**
 * useJournals Hook
 * React Query hook for managing journals list
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import {
  getJournals,
  createJournal,
  updateJournal,
  archiveJournal,
  deleteJournal,
} from '@/services/journalService';
import type { CreateJournalInput, UpdateJournalInput } from '@/types/journal';

/**
 * Fetch all journals for the current user
 */
export function useJournals() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['journals', user?.id],
    queryFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      return getJournals(user.id);
    },
    enabled: !!user?.id,
  });
}

/**
 * Create a new journal
 */
export function useCreateJournal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreateJournalInput) => {
      if (!user?.id) throw new Error('User not authenticated');
      return createJournal(user.id, input);
    },
    onSuccess: (newJournal) => {
      // Invalidate and refetch journals list
      queryClient.invalidateQueries({ queryKey: ['journals', user?.id] });
      toast.success('Journal created successfully');
      return newJournal;
    },
    onError: (error) => {
      console.error('Error creating journal:', error);
      toast.error('Failed to create journal');
    },
  });
}

/**
 * Update a journal
 */
export function useUpdateJournal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      journalId,
      updates,
    }: {
      journalId: string;
      updates: UpdateJournalInput;
    }) => {
      return updateJournal(journalId, updates);
    },
    onMutate: async ({ journalId, updates }) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['journals', user?.id] });
      await queryClient.cancelQueries({ queryKey: ['journal', journalId] });

      // Snapshot previous value
      const previousJournals = queryClient.getQueryData(['journals', user?.id]);
      const previousJournal = queryClient.getQueryData(['journal', journalId]);

      // Optimistically update journals list
      queryClient.setQueryData(['journals', user?.id], (old: any) => {
        if (!old) return old;
        return old.map((journal: any) =>
          journal.id === journalId ? { ...journal, ...updates } : journal
        );
      });

      // Optimistically update single journal
      queryClient.setQueryData(['journal', journalId], (old: any) => {
        if (!old) return old;
        return { ...old, ...updates };
      });

      return { previousJournals, previousJournal };
    },
    onError: (error, { journalId }, context) => {
      // Rollback on error
      if (context?.previousJournals) {
        queryClient.setQueryData(['journals', user?.id], context.previousJournals);
      }
      if (context?.previousJournal) {
        queryClient.setQueryData(['journal', journalId], context.previousJournal);
      }
      console.error('Error updating journal:', error);
      toast.error('Failed to update journal');
    },
    onSettled: (_, __, { journalId }) => {
      // Always refetch after error or success
      queryClient.invalidateQueries({ queryKey: ['journals', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['journal', journalId] });
    },
  });
}

/**
 * Archive a journal
 */
export function useArchiveJournal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: archiveJournal,
    onSuccess: (_, journalId) => {
      queryClient.invalidateQueries({ queryKey: ['journals', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['journal', journalId] });
      toast.success('Journal archived');
    },
    onError: (error) => {
      console.error('Error archiving journal:', error);
      toast.error('Failed to archive journal');
    },
  });
}

/**
 * Delete a journal
 */
export function useDeleteJournal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteJournal,
    onSuccess: (_, journalId) => {
      queryClient.invalidateQueries({ queryKey: ['journals', user?.id] });
      queryClient.removeQueries({ queryKey: ['journal', journalId] });
      queryClient.removeQueries({ queryKey: ['journal-blocks', journalId] });
      toast.success('Journal deleted');
    },
    onError: (error) => {
      console.error('Error deleting journal:', error);
      toast.error('Failed to delete journal');
    },
  });
}





